import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Button,
  Card,
  CardBody,
  Input,
  Select,
  SelectItem,
  Spinner,
  Switch,
  Textarea,
} from '@nextui-org/react';
import {
  AlignLeft,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  CheckCircle2,
  ExternalLink,
  FileText,
  Heading as HeadingIcon,
  Image as ImageIcon,
  List as ListIcon,
  Minus,
  Plus,
  Save,
  Trash2,
  Upload,
  XCircle,
} from 'lucide-react';
import adminPagesService from '../../services/adminPagesService';
import { ContentBlock, DynamicPage } from '../../services/dynamicPageService';

const ACCENT = '#3fced1';

const inputClasses = {
  inputWrapper: 'bg-zinc-800 border border-zinc-700',
  input: 'text-white',
  label: 'text-gray-400',
};

const newBlock = (type: ContentBlock['type']): ContentBlock => ({
  id: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `block-${Date.now()}`,
  type,
  text: '',
  level: 2,
  imageUrl: '',
  imageAlt: '',
  imageCaption: '',
  imageMaxWidth: null,
  listItems: [],
  listType: 'bullet',
  order: 0,
});

const BLOCK_TYPES: { type: ContentBlock['type']; label: string; icon: React.ReactNode }[] = [
  { type: 'heading', label: 'Überschrift', icon: <HeadingIcon size={15} /> },
  { type: 'paragraph', label: 'Absatz', icon: <AlignLeft size={15} /> },
  { type: 'image', label: 'Bild', icon: <ImageIcon size={15} /> },
  { type: 'list', label: 'Liste', icon: <ListIcon size={15} /> },
  { type: 'divider', label: 'Trennlinie', icon: <Minus size={15} /> },
];

const blockMeta = (type: string) =>
  BLOCK_TYPES.find((b) => b.type === type) ?? { type, label: type, icon: <AlignLeft size={15} /> };

const FeedbackBanner: React.FC<{
  message: string | null;
  type: 'success' | 'error';
  onClose: () => void;
}> = ({ message, type, onClose }) => {
  if (!message) return null;
  const isSuccess = type === 'success';
  return (
    <div
      className={`mb-4 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${
        isSuccess
          ? 'border-[#3fced1]/40 bg-[#3fced1]/10 text-[#3fced1]'
          : 'border-red-500/40 bg-red-500/10 text-red-400'
      }`}
    >
      {isSuccess ? <CheckCircle2 size={18} className="mt-0.5 shrink-0" /> : <XCircle size={18} className="mt-0.5 shrink-0" />}
      <span className="flex-1">{message}</span>
      <button type="button" onClick={onClose} aria-label="Hinweis schließen" className="opacity-70 hover:opacity-100">✕</button>
    </div>
  );
};

// =====================================================================
// Block-Editor-Karte
// =====================================================================

const BlockCard: React.FC<{
  block: ContentBlock;
  index: number;
  total: number;
  onChange: (patch: Partial<ContentBlock>) => void;
  onMove: (direction: -1 | 1) => void;
  onDelete: () => void;
  onError: (msg: string) => void;
}> = ({ block, index, total, onChange, onMove, onDelete, onError }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const meta = blockMeta(block.type);
  const preview = adminPagesService.buildFileUrl(block.imageUrl);

  const handleImageFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      onError('Bitte eine Bilddatei auswählen (PNG/JPG/WebP).');
      return;
    }
    setUploading(true);
    try {
      const url = await adminPagesService.uploadImage(file);
      onChange({ imageUrl: url });
    } catch (err) {
      onError(adminPagesService.getErrorMessage(err, 'Bild-Upload fehlgeschlagen.'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="border border-zinc-800 bg-zinc-900">
      <CardBody className="gap-3 p-4">
        {/* Kopf: Typ + Aktionen */}
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 rounded-full bg-[#3fced1]/10 px-2.5 py-1 text-xs font-semibold text-[#3fced1]">
            {meta.icon}
            {meta.label}
          </span>
          <span className="text-xs text-gray-600">#{index + 1}</span>
          <div className="ml-auto flex items-center gap-1">
            <Button isIconOnly size="sm" variant="light" aria-label="Nach oben"
              isDisabled={index === 0} className="text-gray-500" onPress={() => onMove(-1)}>
              <ArrowUp size={14} />
            </Button>
            <Button isIconOnly size="sm" variant="light" aria-label="Nach unten"
              isDisabled={index === total - 1} className="text-gray-500" onPress={() => onMove(1)}>
              <ArrowDown size={14} />
            </Button>
            <Button isIconOnly size="sm" variant="light" aria-label="Block löschen"
              className="text-gray-500 hover:text-red-400" onPress={onDelete}>
              <Trash2 size={14} />
            </Button>
          </div>
        </div>

        {/* Typ-spezifische Felder */}
        {block.type === 'heading' && (
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input
              label="Überschrift"
              value={block.text}
              onValueChange={(v) => onChange({ text: v })}
              classNames={inputClasses}
            />
            <Select
              label="Ebene"
              selectedKeys={[String(block.level || 2)]}
              onSelectionChange={(keys) => onChange({ level: parseInt(String(Array.from(keys)[0]), 10) || 2 })}
              className="sm:max-w-[140px]"
              classNames={{ trigger: 'bg-zinc-800 border border-zinc-700', label: 'text-gray-400', value: 'text-white' }}
            >
              <SelectItem key="1">H1</SelectItem>
              <SelectItem key="2">H2</SelectItem>
              <SelectItem key="3">H3</SelectItem>
            </Select>
          </div>
        )}

        {block.type === 'paragraph' && (
          <Textarea
            label="Text"
            value={block.text}
            onValueChange={(v) => onChange({ text: v })}
            minRows={3}
            classNames={inputClasses}
          />
        )}

        {block.type === 'image' && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex h-20 w-32 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-zinc-700 bg-zinc-950">
                {preview ? (
                  <img src={preview} alt="" className="h-full w-full object-cover" />
                ) : (
                  <ImageIcon size={18} className="text-zinc-700" />
                )}
              </div>
              <Button
                size="sm"
                variant="bordered"
                className="border-zinc-700 text-gray-300"
                isLoading={uploading}
                startContent={!uploading && <Upload size={14} />}
                onPress={() => fileRef.current?.click()}
              >
                {block.imageUrl ? 'Bild ersetzen' : 'Bild hochladen'}
              </Button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageFile} />
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Input label="Alt-Text" value={block.imageAlt}
                onValueChange={(v) => onChange({ imageAlt: v })} classNames={inputClasses} />
              <Input label="Bildunterschrift" value={block.imageCaption}
                onValueChange={(v) => onChange({ imageCaption: v })} classNames={inputClasses} />
              <Input label="Max. Breite (px, optional)" type="number"
                value={block.imageMaxWidth != null ? String(block.imageMaxWidth) : ''}
                onValueChange={(v) => {
                  const n = parseInt(v, 10);
                  onChange({ imageMaxWidth: isNaN(n) ? null : n });
                }}
                classNames={inputClasses} />
            </div>
          </div>
        )}

        {block.type === 'list' && (
          <div className="space-y-3">
            <Select
              label="Listen-Typ"
              selectedKeys={[block.listType || 'bullet']}
              onSelectionChange={(keys) => onChange({ listType: String(Array.from(keys)[0]) as any })}
              className="max-w-[200px]"
              classNames={{ trigger: 'bg-zinc-800 border border-zinc-700', label: 'text-gray-400', value: 'text-white' }}
            >
              <SelectItem key="bullet">Aufzählung (•)</SelectItem>
              <SelectItem key="numbered">Nummeriert (1., 2., …)</SelectItem>
            </Select>
            <Textarea
              label="Einträge (einer pro Zeile)"
              value={(block.listItems ?? []).join('\n')}
              onValueChange={(v) => onChange({ listItems: v.split('\n') })}
              minRows={3}
              classNames={inputClasses}
            />
          </div>
        )}

        {block.type === 'divider' && (
          <div className="border-t border-dashed border-zinc-700" />
        )}
      </CardBody>
    </Card>
  );
};

// =====================================================================
// Seite
// =====================================================================

const AdminPageEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [page, setPage] = useState<DynamicPage | null>(null);
  const [blocks, setBlocks] = useState<ContentBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const data = await adminPagesService.getPage(id);
      if (!data) {
        setError('Seite nicht gefunden.');
      } else {
        setPage(data);
        setBlocks([...data.contentBlocks].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
      }
    } catch (err) {
      setError(adminPagesService.getErrorMessage(err, 'Seite konnte nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const setPageField = (patch: Partial<DynamicPage>) =>
    setPage((p) => (p ? { ...p, ...patch } : p));

  const updateBlock = (index: number, patch: Partial<ContentBlock>) =>
    setBlocks((prev) => prev.map((b, i) => (i === index ? { ...b, ...patch } : b)));

  const moveBlock = (index: number, direction: -1 | 1) =>
    setBlocks((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const deleteBlock = (index: number) =>
    setBlocks((prev) => prev.filter((_, i) => i !== index));

  const addBlock = (type: ContentBlock['type']) =>
    setBlocks((prev) => [...prev, newBlock(type)]);

  const handleSave = async () => {
    if (!page || !id) return;
    if (!page.title.trim() || !page.slug.trim()) {
      setError('Titel und Slug sind erforderlich.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Listen-Einträge beim Speichern säubern (leere Zeilen raus)
      const cleanedBlocks = blocks.map((b) =>
        b.type === 'list'
          ? { ...b, listItems: (b.listItems ?? []).map((x) => x.trim()).filter(Boolean) }
          : b
      );
      const updated = await adminPagesService.updatePage(id, page, cleanedBlocks);
      if (updated) {
        setPage(updated);
        setBlocks([...updated.contentBlocks].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
      }
      setFeedback('Seite gespeichert.');
    } catch (err) {
      setError(adminPagesService.getErrorMessage(err, 'Speichern fehlgeschlagen.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (!page) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <FileText size={40} className="mx-auto mb-3 text-zinc-700" />
        <p className="text-gray-400">{error || 'Seite nicht gefunden.'}</p>
        <Button as={RouterLink} to="/admin/seiten" className="mt-4 bg-[#3fced1] font-semibold text-black">
          Zurück zur Übersicht
        </Button>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{page.title} | Seiten-Admin | DartCloud</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        {/* Kopf */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <Button
            as={RouterLink}
            to="/admin/seiten"
            isIconOnly
            variant="bordered"
            className="border-zinc-700 text-gray-300"
            aria-label="Zurück zur Seiten-Übersicht"
          >
            <ArrowLeft size={18} />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-xl font-extrabold tracking-tight text-white sm:text-2xl">
              {page.title}
            </h1>
            <p className="text-xs text-gray-500">/dartcloud/{page.slug}</p>
          </div>
          <Button
            as="a"
            href={`/dartcloud/${page.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            variant="bordered"
            className="border-zinc-700 text-gray-300"
            endContent={<ExternalLink size={13} />}
          >
            Ansehen
          </Button>
          <Button
            className="bg-[#3fced1] font-semibold text-black"
            isLoading={saving}
            startContent={!saving && <Save size={15} />}
            onPress={handleSave}
          >
            Speichern
          </Button>
        </div>

        <FeedbackBanner message={feedback} type="success" onClose={() => setFeedback(null)} />
        <FeedbackBanner message={error} type="error" onClose={() => setError(null)} />

        {/* Seiten-Metadaten */}
        <Card className="mb-6 border border-zinc-800 bg-zinc-900">
          <CardBody className="grid gap-4 p-4 sm:grid-cols-2">
            <Input label="Titel" value={page.title}
              onValueChange={(v) => setPageField({ title: v })} classNames={inputClasses} />
            <Input label="Slug (URL)" value={page.slug}
              onValueChange={(v) => setPageField({ slug: v })} classNames={inputClasses} />
            <Textarea label="Meta-Description (SEO)" value={page.metaDescription}
              onValueChange={(v) => setPageField({ metaDescription: v })}
              minRows={2} className="sm:col-span-2" classNames={inputClasses} />
            <div className="flex items-center justify-between gap-4 rounded-xl border border-zinc-800 bg-zinc-950/60 px-3 py-2.5">
              <div>
                <p className="text-sm font-semibold text-white">Sichtbar</p>
                <p className="text-xs text-gray-400">Seite ist öffentlich erreichbar.</p>
              </div>
              <Switch isSelected={page.isVisible}
                onValueChange={(v) => setPageField({ isVisible: v })} aria-label="Sichtbar" />
            </div>
            <div className="flex items-center justify-between gap-4 rounded-xl border border-zinc-800 bg-zinc-950/60 px-3 py-2.5">
              <div>
                <p className="text-sm font-semibold text-white">"Zuletzt aktualisiert" anzeigen</p>
                <p className="text-xs text-gray-400">Datum der letzten Änderung auf der Seite.</p>
              </div>
              <Switch isSelected={page.showLastUpdated}
                onValueChange={(v) => setPageField({ showLastUpdated: v })} aria-label="Zuletzt aktualisiert anzeigen" />
            </div>
          </CardBody>
        </Card>

        {/* Blöcke */}
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-lg font-bold text-white">Inhalt ({blocks.length} Blöcke)</h2>
        </div>
        <div className="space-y-3">
          {blocks.map((block, index) => (
            <BlockCard
              key={block.id || index}
              block={block}
              index={index}
              total={blocks.length}
              onChange={(patch) => updateBlock(index, patch)}
              onMove={(dir) => moveBlock(index, dir)}
              onDelete={() => deleteBlock(index)}
              onError={setError}
            />
          ))}
          {blocks.length === 0 && (
            <p className="rounded-xl border border-dashed border-zinc-700 py-8 text-center text-sm text-gray-500">
              Noch keine Inhalte — füge unten den ersten Block hinzu.
            </p>
          )}
        </div>

        {/* Block hinzufügen */}
        <div className="mt-4 flex flex-wrap gap-2">
          {BLOCK_TYPES.map((b) => (
            <button
              key={b.type}
              type="button"
              onClick={() => addBlock(b.type)}
              className="inline-flex items-center gap-1.5 rounded-full border border-zinc-700 px-3.5 py-2 text-sm font-medium text-gray-300 transition-colors hover:border-[#3fced1]/60 hover:text-white"
            >
              <Plus size={13} style={{ color: ACCENT }} />
              {b.icon}
              {b.label}
            </button>
          ))}
        </div>

        {/* Speichern unten (bei langen Seiten) */}
        <div className="mt-8 flex justify-end">
          <Button
            className="bg-[#3fced1] font-semibold text-black"
            isLoading={saving}
            startContent={!saving && <Save size={15} />}
            onPress={handleSave}
          >
            Speichern
          </Button>
        </div>
      </div>
    </>
  );
};

export default AdminPageEditorPage;
